import { LocationDto } from '@trucktrax/trucktrax-ts-common';

/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param coord1 First coordinate (lat/lng)
 * @param coord2 Second coordinate (lat/lng)
 * @returns Distance in miles, or null if coordinates are invalid
 */
export function calculateDistanceInMiles(coord1: LocationDto, coord2: LocationDto): number | null {
  const lat1 = coord1.latitude ?? coord1.lat;
  const lng1 = coord1.longitude ?? coord1.lng;
  const lat2 = coord2.latitude ?? coord2.lat;
  const lng2 = coord2.longitude ?? coord2.lng;

  // Return null if any coordinate is missing/invalid
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) {
    return null;
  }

  // Convert degrees to radians
  const toRadians = (degrees: number) => degrees * (Math.PI / 180);

  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in miles
}

/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param coord1 First coordinate
 * @param coord2 Second coordinate
 * @returns Distance in meters, or null if coordinates are invalid
 */
export function calculateDistanceInMeters(
  coord1: { lat: number; lng: number },
  coord2: { lat: number; lng: number },
): number | null {
  // Return null if any coordinate is missing/invalid
  if (coord1.lat == null || coord1.lng == null || coord2.lat == null || coord2.lng == null) {
    return null;
  }

  const R = 6371e3; // Earth radius in meters
  const φ1 = (coord1.lat * Math.PI) / 180;
  const φ2 = (coord2.lat * Math.PI) / 180;
  const Δφ = ((coord2.lat - coord1.lat) * Math.PI) / 180;
  const Δλ = ((coord2.lng - coord1.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Convert miles to meters
 */
export function milesToMeters(miles: number): number {
  return miles * 1609.344;
}

/**
 * Convert meters to miles
 */
export function metersToMiles(meters: number): number {
  return meters / 1609.344;
}
